import React, { useRef, useEffect } from 'react';
import { Lock, Unlock, Trash2, Group, Ungroup } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useEditorStore } from '@/core/stores/editor-store';
import { useStageStore } from '@/core/stores/stage-store';
import { useHistoryStore } from '@/core/stores/history-store';
import {
  NumberInput,
  SliderInput,
  SelectInput,
  ToggleInput,
  TextInput,
  PanelSection,
  IconButton,
} from '@/ui/shared';
import type { Wall, WallType, WallMaterial, StageObject, ScoringZone, ActivationType } from '@/stage/types';

const PropertiesPanel: React.FC = () => {
  const { selectedObjectIds } = useEditorStore();
  const { layout, updateObject, removeObject, updateWall, removeWall } = useStageStore();
  const historyStore = useHistoryStore();

  // Track whether we've already pushed history for the current edit session.
  // Resets when the selection changes so the first property edit after selecting
  // a new object is always undoable as a single step.
  const hasPushedHistory = useRef(false);
  useEffect(() => {
    hasPushedHistory.current = false;
  }, [selectedObjectIds]);

  if (!layout) {
    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex items-center justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">No stage loaded</p>
      </div>
    );
  }

  if (selectedObjectIds.length === 0) {
    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex items-center justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">Select an object to edit</p>
      </div>
    );
  }

  if (selectedObjectIds.length > 1) {
    const selectedObjs = layout.objects.filter((o) =>
      selectedObjectIds.includes(o.id),
    );
    const groupIds = new Set(selectedObjs.map((o) => o.group_id).filter(Boolean));
    const anyGrouped = groupIds.size > 0;
    const allSameGroup = groupIds.size === 1 && selectedObjs.every((o) => o.group_id);

    const groupAll = () => {
      if (layout) historyStore.pushState(layout);
      const id = uuidv4();
      for (const o of selectedObjs) {
        updateObject(o.id, { group_id: id });
      }
      useEditorStore.getState().setDirty(true);
    };

    const ungroupAll = () => {
      if (layout) historyStore.pushState(layout);
      for (const o of selectedObjs) {
        updateObject(o.id, { group_id: undefined });
      }
      useEditorStore.getState().setDirty(true);
    };

    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div className="p-3 space-y-3">
          <div className="bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-800 rounded p-2">
            <p className="text-xs text-blue-900 dark:text-blue-100">
              {selectedObjectIds.length} objects selected
              {allSameGroup && ' · same group'}
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={groupAll}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors"
            >
              <Group size={14} />
              Group
            </button>
            <button
              onClick={ungroupAll}
              disabled={!anyGrouped}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Ungroup size={14} />
              Ungroup
            </button>
          </div>

          <button
            onClick={() => {
              selectedObjectIds.forEach((id) => removeObject(id));
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors"
          >
            <Trash2 size={14} />
            Delete All
          </button>
        </div>
      </div>
    );
  }

  const objectId = selectedObjectIds[0];
  const stageObject = layout.objects.find((obj) => obj.id === objectId);

  // Walls live on a parallel list. If the selected id isn't a stage object,
  // look for a matching wall and render a dedicated wall-edit panel.
  const wall = !stageObject ? layout.walls.find((w) => w.id === objectId) : undefined;

  if (!stageObject && wall) {
    const pushHistoryOnce = () => {
      if (!hasPushedHistory.current && layout) {
        historyStore.pushState(layout);
        hasPushedHistory.current = true;
      }
    };
    const updateWallAndTouch = (patch: Partial<Wall>) => {
      pushHistoryOnce();
      updateWall(wall.id, patch);
      useEditorStore.getState().setDirty(true);
    };

    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div className="p-3 space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 capitalize">
              {wall.type.replace(/_/g, ' ')}
            </h2>
          </div>

          <PanelSection title="Wall" defaultOpen={true}>
            <SelectInput
              label="Type"
              value={wall.type}
              onChange={(value) => updateWallAndTouch({ type: value as WallType })}
              options={[
                { value: 'wall', label: 'Wall' },
                { value: 'berm', label: 'Berm' },
                { value: 'vision_barrier', label: 'Vision barrier' },
                { value: 'mesh_fence', label: 'Mesh fence' },
                { value: 'port_wall', label: 'Port wall' },
                { value: 'barrel_stack', label: 'Barrel stack' },
              ]}
            />
            <SelectInput
              label="Material"
              value={wall.material}
              onChange={(value) => updateWallAndTouch({ material: value as WallMaterial })}
              options={[
                { value: 'plywood', label: 'Plywood' },
                { value: 'metal', label: 'Metal' },
                { value: 'mesh', label: 'Mesh' },
                { value: 'concrete', label: 'Concrete' },
              ]}
            />
            <NumberInput
              label="Height (m)"
              value={wall.height_m}
              onChange={(value) => updateWallAndTouch({ height_m: Math.max(0.1, value) })}
              min={0.1}
              max={10}
              step={0.1}
            />
            <ToggleInput
              label="Has port / opening"
              value={wall.has_port}
              onChange={(value) => {
                if (value && !wall.port_config) {
                  updateWallAndTouch({
                    has_port: true,
                    port_config: { height_m: 0.5, width_m: 1, offset_from_ground_m: 0.8 },
                  });
                } else {
                  updateWallAndTouch({ has_port: value });
                }
              }}
            />
            {wall.has_port && wall.port_config && (
              <>
                <NumberInput
                  label="Port width (m)"
                  value={wall.port_config.width_m}
                  onChange={(value) =>
                    updateWallAndTouch({
                      port_config: { ...wall.port_config!, width_m: Math.max(0.1, value) },
                    })
                  }
                  min={0.1}
                  step={0.1}
                />
                <NumberInput
                  label="Port height (m)"
                  value={wall.port_config.height_m}
                  onChange={(value) =>
                    updateWallAndTouch({
                      port_config: { ...wall.port_config!, height_m: Math.max(0.1, value) },
                    })
                  }
                  min={0.1}
                  step={0.1}
                />
                <NumberInput
                  label="Port offset from ground (m)"
                  value={wall.port_config.offset_from_ground_m}
                  onChange={(value) =>
                    updateWallAndTouch({
                      port_config: { ...wall.port_config!, offset_from_ground_m: Math.max(0, value) },
                    })
                  }
                  min={0}
                  step={0.1}
                />
              </>
            )}
          </PanelSection>

          <button
            onClick={() => {
              if (layout) historyStore.pushState(layout);
              removeWall(wall.id);
              useEditorStore.getState().setDirty(true);
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors mt-4"
          >
            <Trash2 size={14} />
            Delete Wall
          </button>
        </div>
      </div>
    );
  }

  if (!stageObject) {
    return (
      <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 flex items-center justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">Object not found</p>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<StageObject>) => {
    // Push pre-edit state to history once per edit session (resets on selection change)
    if (!hasPushedHistory.current && layout) {
      historyStore.pushState(layout);
      hasPushedHistory.current = true;
    }
    updateObject(objectId, updates);
    useEditorStore.getState().setDirty(true);
  };

  const canvasW = layout.canvas.width_m;
  const canvasH = layout.canvas.height_m;
  const clampX = (x: number) => Math.max(0, Math.min(canvasW, x));
  const clampY = (y: number) => Math.max(0, Math.min(canvasH, y));

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="p-3 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 capitalize">
            {stageObject.type.replace(/_/g, ' ')}
          </h2>
          <IconButton
            icon={
              stageObject.locked ? (
                <Lock size={16} />
              ) : (
                <Unlock size={16} />
              )
            }
            tooltip={stageObject.locked ? 'Locked' : 'Unlocked'}
            onClick={() =>
              handleUpdate({ locked: !stageObject.locked })
            }
            size="sm"
          />
        </div>

        {/* Position and Rotation */}
        <PanelSection title="Transform" defaultOpen={true}>
          <NumberInput
            label="Position X (m)"
            value={stageObject.position.x || 0}
            onChange={(value) => handleUpdate({ position: { ...stageObject.position, x: clampX(value) } })}
            min={0}
            max={canvasW}
            step={0.1}
          />
          <NumberInput
            label="Position Y (m)"
            value={stageObject.position.y || 0}
            onChange={(value) => handleUpdate({ position: { ...stageObject.position, y: clampY(value) } })}
            min={0}
            max={canvasH}
            step={0.1}
          />
          <NumberInput
            label="Position Z (m)"
            value={stageObject.position.z || 0}
            onChange={(value) => handleUpdate({ position: { ...stageObject.position, z: Math.max(0, value) } })}
            min={0}
            step={0.1}
          />
          <SliderInput
            label="Rotation (degrees)"
            value={stageObject.rotation?.z || 0}
            onChange={(value) =>
              handleUpdate({ rotation: { x: 0, y: 0, z: value } })
            }
            min={0}
            max={360}
            step={1}
          />
        </PanelSection>

        {/* Scale */}
        <PanelSection title="Scale" defaultOpen={false}>
          <NumberInput
            label="Scale X"
            value={stageObject.scale.x || 1}
            onChange={(value) =>
              handleUpdate({ scale: { ...stageObject.scale, x: Math.max(0.05, value) } })
            }
            min={0.05}
            step={0.1}
          />
          <NumberInput
            label="Scale Y"
            value={stageObject.scale.y || 1}
            onChange={(value) =>
              handleUpdate({ scale: { ...stageObject.scale, y: Math.max(0.05, value) } })
            }
            min={0.05}
            step={0.1}
          />
        </PanelSection>

        {/* Target-specific properties */}
        {(stageObject.type === 'paper_target' ||
          stageObject.type === 'no_shoot' ||
          stageObject.type === 'steel_target' ||
          stageObject.type === 'popper') && (
          <PanelSection title="Target Properties" defaultOpen={true}>
            <SelectInput
              label="Scoring Zone"
              value={stageObject.scoring_zone || 'full'}
              onChange={(value) =>
                handleUpdate({
                  scoring_zone: value as ScoringZone,
                })
              }
              options={[
                { value: 'full', label: 'Full' },
                { value: 'partial_head', label: 'Partial (Head)' },
                { value: 'partial_body', label: 'Partial (Body)' },
              ]}
            />
            <ToggleInput
              label="On Stand"
              value={stageObject.on_stand || false}
              onChange={(value) =>
                handleUpdate({
                  on_stand: value,
                })
              }
            />
            {stageObject.on_stand && (
              <NumberInput
                label="Stand Height (m)"
                value={stageObject.stand_height_m || 0}
                onChange={(value) =>
                  handleUpdate({
                    stand_height_m: Math.max(0, value),
                  })
                }
                min={0}
                max={3}
                step={0.1}
              />
            )}
            <SliderInput
              label="Facing Direction (degrees)"
              value={stageObject.facing_direction || 0}
              onChange={(value) =>
                handleUpdate({
                  facing_direction: value,
                })
              }
              min={0}
              max={360}
              step={1}
            />
          </PanelSection>
        )}

        {/* Activation config — moving targets only */}
        {(stageObject.type === 'swinger' ||
          stageObject.type === 'drop_turner' ||
          stageObject.type === 'texas_star' ||
          stageObject.type === 'bobber' ||
          stageObject.type === 'paper_swinger' ||
          stageObject.type === 'paper_swinger_once' ||
          stageObject.type === 'paper_popup') && (
          <PanelSection title="Activation" defaultOpen={false}>
            <SelectInput
              label="Activation type"
              value={stageObject.activation?.type || stageObject.type}
              onChange={(value) =>
                handleUpdate({
                  activation: { ...stageObject.activation, type: value as ActivationType },
                })
              }
              options={[
                { value: 'static', label: 'Static' },
                { value: 'swinger', label: 'Swinger' },
                { value: 'drop_turner', label: 'Drop turner' },
                { value: 'bobber', label: 'Bobber' },
              ]}
            />
            {(stageObject.activation?.type === 'swinger' ||
              stageObject.type === 'swinger' ||
              stageObject.type === 'texas_star' ||
              stageObject.type === 'paper_swinger' ||
              stageObject.type === 'paper_swinger_once') && (
              <NumberInput
                label={
                  stageObject.type === 'paper_swinger_once'
                    ? 'Swing angle (deg, +right / -left)'
                    : 'Swing arc (deg)'
                }
                value={stageObject.activation?.swing_arc_degrees || 90}
                onChange={(value) =>
                  handleUpdate({
                    activation: {
                      ...stageObject.activation,
                      type: stageObject.activation?.type || 'swinger',
                      // One-time swinger allows signed arcs (±180); others
                      // are unsigned magnitudes.
                      swing_arc_degrees:
                        stageObject.type === 'paper_swinger_once'
                          ? Math.max(-180, Math.min(180, value))
                          : Math.max(0, Math.min(360, value)),
                    },
                  })
                }
                min={stageObject.type === 'paper_swinger_once' ? -180 : 0}
                max={stageObject.type === 'paper_swinger_once' ? 180 : 360}
                step={5}
              />
            )}
            {(stageObject.activation?.type === 'drop_turner' ||
              stageObject.type === 'drop_turner') && (
              <NumberInput
                label="Exposure time (s)"
                value={stageObject.activation?.exposure_time || 2}
                onChange={(value) =>
                  handleUpdate({
                    activation: {
                      ...stageObject.activation,
                      type: stageObject.activation?.type || 'drop_turner',
                      exposure_time: Math.max(0, value),
                    },
                  })
                }
                min={0}
                step={0.1}
              />
            )}
            {(stageObject.activation?.type === 'bobber' ||
              stageObject.type === 'bobber' ||
              stageObject.type === 'paper_popup') && (
              <NumberInput
                label="Cycle time (s)"
                value={stageObject.activation?.timing_seconds || 3}
                onChange={(value) =>
                  handleUpdate({
                    activation: {
                      ...stageObject.activation,
                      type: stageObject.activation?.type || 'bobber',
                      timing_seconds: Math.max(0, value),
                    },
                  })
                }
                min={0}
                step={0.1}
              />
            )}
            <TextInput
              label="Trigger"
              value={stageObject.activation?.trigger || ''}
              onChange={(value) =>
                handleUpdate({
                  activation: {
                    ...stageObject.activation,
                    type: stageObject.activation?.type || 'static',
                    trigger: value,
                  },
                })
              }
              placeholder="e.g., first shot, popper-1 falls"
            />
          </PanelSection>
        )}

        {/* Label */}
        <PanelSection title="Identification" defaultOpen={true}>
          <TextInput
            label="Label"
            value={stageObject.label || ''}
            onChange={(value) => handleUpdate({ label: value })}
            placeholder="e.g., T1, Target A"
          />
          {stageObject.group_id && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Group: {stageObject.group_id.slice(0, 6)}…
              </span>
              <button
                onClick={() => handleUpdate({ group_id: undefined })}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded"
              >
                <Ungroup size={12} />
                Ungroup
              </button>
            </div>
          )}
        </PanelSection>

        {/* Annotations - Note: text_label and measurement_line are handled via Annotations in StageLayout, not StageObject */}

        {/* Delete button */}
        <button
          onClick={() => removeObject(objectId)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-xs rounded transition-colors mt-4"
        >
          <Trash2 size={14} />
          Delete Object
        </button>
      </div>
    </div>
  );
};

export default PropertiesPanel;
